import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { faqData } from "@/data/faqData";
import { trackPhoneCall } from "@/lib/googleAds";
import FAQSchema from "@/components/FAQSchema";

interface FAQSectionProps {
  serviceSlug: string;
  serviceName: string;
}

export default function FAQSection({ serviceSlug, serviceName }: FAQSectionProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const faqs = faqData[serviceSlug] || [];

  if (!faqs.length) return null;

  return (
    <>
      {/* FAQ Schema for SEO */}
      <FAQSchema faqs={faqs} />
      
      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container px-4">
          <div className="text-center mb-12">
            <p className="text-[#1e3a8a] font-semibold mb-2 text-sm tracking-wider">
              FREQUENTLY ASKED QUESTIONS
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0a1628] mb-4">
              {serviceName} FAQ
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about our {serviceName.toLowerCase()} services.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg border border-gray-200 hover:border-[#4caf30]/30 transition-colors"
              >
                <button
                  onClick={() =>
                    setExpandedIndex(expandedIndex === index ? null : index)
                  }
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-left font-semibold text-[#0a1628] text-sm md:text-base">
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 text-[#1e3a8a] flex-shrink-0 ml-4 transition-transform ${
                      expandedIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {expandedIndex === index && (
                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Have more questions? Contact us for a free consultation.
            </p>
            <a
              href="tel:805-403-8727"
              onClick={trackPhoneCall}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#4caf30] text-white rounded-lg hover:bg-[#3d9426] transition-colors font-semibold"
            >
              Call: 805-403-8727
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
